import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { CREDENTIALS_REQUEST, CREDENTIALS_RESPONSE } from '@rrsolutionsit/atl-chatbot-lib';
import { authClient } from './auth-client';
import { User } from '../models/user.model';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly router = inject(Router);

  private readonly currentUser = signal<User | null>(null);
  /**
   * The session token, for the AI backend: handed to the chatbot on request and sent as a bearer
   * token by the CRM's own calls to that API. The AI backend looks this exact value up against the
   * live session table, so it is passed through verbatim.
   */
  private sessionToken: string | null = null;
  readonly loading = signal(true);

  readonly user = this.currentUser.asReadonly();
  readonly isAuthenticated = computed(() => !!this.currentUser());

  constructor() {
    this.fetchSession();

    window.addEventListener('message', (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.data?.type !== CREDENTIALS_REQUEST) {
        return;
      }
      this.publishChatbotCredentials();
    });
  }

  bearerToken(): string | null {
    return this.sessionToken;
  }

  async fetchSession() {
    try {
      const data = await authClient.getSession();
      if (data?.user) {
        this.currentUser.set(data.user as User);
        this.sessionToken = data.session?.token ?? null;
        this.publishChatbotCredentials();
      }
    } catch {
      this.currentUser.set(null);
      this.sessionToken = null;
    } finally {
      this.loading.set(false);
    }
  }

  async login(email: string, password: string) {
    const data = await authClient.signIn(email, password);
    if (data?.user) {
      this.currentUser.set(data.user as User);
      // Unlike `get-session`, `sign-in/email` returns the token at the top level, not nested
      // under `session`.
      this.sessionToken = (data as { token?: string }).token ?? null;
      this.publishChatbotCredentials();
    }
  }

  /**
   * Hands the current session token to the chatbot, which listens for this same message on this
   * very window. Only the token: the AI backend reads who the caller is out of it, so an account
   * id sent alongside would be a value nothing checks.
   */
  private publishChatbotCredentials(): void {
    if (!this.currentUser() || !this.sessionToken) {
      return;
    }
    window.postMessage({ type: CREDENTIALS_RESPONSE, token: this.sessionToken }, window.location.origin);
  }

  async logout() {
    await authClient.signOut();
    this.currentUser.set(null);
    this.sessionToken = null;
    this.router.navigate(['/login']);
  }

  /** Update the signed-in user's own profile, then refresh the cached session. */
  async updateProfile(values: { name?: string }) {
    await authClient.updateUser(values);
    await this.fetchSession();
  }

  /** Change the signed-in user's own password. */
  async changePassword(currentPassword: string, newPassword: string) {
    await authClient.changePassword(currentPassword, newPassword);
  }
}
